import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { X } from "lucide-react";

function AddUserModal({ role, user = null, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
    password: "",
  });
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const isEditMode = !!user;
  const roleLabel = role === "branch_admin" ? "Branch Admin" : "Nurse";

  // Get user role from localStorage
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const userRole = userDetails.role;

  // Fetch branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        console.log("Fetching branches...");
        const response = await axiosInstance.get("/hospitals/branches");
        console.log("Branches response:", response.data);
        if (response.data.success) {
          setBranches(response.data.branches);
          console.log("Branches set:", response.data.branches);

          // Auto-select if only one branch
          if (response.data.branches.length === 1) {
            setSelectedBranches([response.data.branches[0].id]);
            console.log(
              "Auto-selected single branch:",
              response.data.branches[0].id
            );
          }
        } else {
          console.error("Failed to fetch branches:", response.data);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
        console.error("Error details:", error.response?.data);
      }
    };
    // Only fetch branches for hospital_admin in add mode
    if (!isEditMode && userRole === "hospital_admin") {
      fetchBranches();
    }
  }, [isEditMode, userRole]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBranchToggle = (branchId) => {
    setSelectedBranches((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId]
    );
  };

  const getSelectedBranchNames = () => {
    if (selectedBranches.length === 0) return "Select branches";
    if (selectedBranches.length === branches.length)
      return "All branches selected";
    return `${selectedBranches.length} branch(es) selected`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditMode) {
        // Update user
        const response = await axiosInstance.put(`/users/${user.id}`, formData);
        if (response.data.success) {
          alert(`${roleLabel} updated successfully`);
          onSuccess();
        }
      } else {
        // Create user
        // Branch admins don't need to select branches (use their own branch)
        if (userRole === "hospital_admin" && selectedBranches.length === 0) {
          alert("Please select at least one branch");
          setLoading(false);
          return;
        }

        const data = {
          ...formData,
          role,
          ...(userRole === "hospital_admin" && {
            branchIds: JSON.stringify(selectedBranches),
          }),
        };

        const response = await axiosInstance.post("/users", data);
        if (response.data.success) {
          alert(response.data.message);
          onSuccess();
        }
      }
    } catch (error) {
      console.error(
        `Error ${
          isEditMode ? "updating" : "creating"
        } ${roleLabel.toLowerCase()}:`,
        error
      );
      alert(
        error.response?.data?.message ||
          `Failed to ${
            isEditMode ? "update" : "create"
          } ${roleLabel.toLowerCase()}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEditMode ? `Edit ${roleLabel}` : `Add ${roleLabel}`}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={isEditMode}
            />
            {!isEditMode && selectedBranches.length > 1 && (
              <p className="text-xs text-gray-500 mt-1">
                Note: For multiple branches, email will be modified with branch
                ID
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Password {!isEditMode && "*"}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required={!isEditMode}
            />
            {isEditMode && (
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to keep current password
              </p>
            )}
          </div>

          {/* Branch info for branch_admin */}
          {!isEditMode && userRole === "branch_admin" && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> The nurse will be created in your branch.
              </p>
            </div>
          )}

          {/* Branch Selection - Only for hospital_admin in add mode */}
          {!isEditMode && userRole === "hospital_admin" && (
            <div ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {branches.length === 1 ? "Branch" : "Select Branches *"}
              </label>

              {/* Single branch - show as text/readonly */}
              {branches.length === 1 ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                  {branches[0].name}
                </div>
              ) : (
                /* Multiple branches - show dropdown */
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-3 py-2 text-left border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white flex items-center justify-between"
                  >
                    <span
                      className={
                        selectedBranches.length === 0
                          ? "text-gray-400"
                          : "text-gray-700"
                      }
                    >
                      {getSelectedBranchNames()}
                    </span>
                    <svg
                      className={`w-5 h-5 transition-transform ${
                        isDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {branches.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No branches available
                        </div>
                      ) : (
                        <>
                          <div className="sticky top-0 bg-gray-50 px-3 py-2 border-b border-gray-200">
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  selectedBranches.length === branches.length
                                ) {
                                  setSelectedBranches([]);
                                } else {
                                  setSelectedBranches(
                                    branches.map((b) => b.id)
                                  );
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {selectedBranches.length === branches.length
                                ? "Deselect All"
                                : "Select All"}
                            </button>
                          </div>
                          {branches.map((branch) => (
                            <label
                              key={branch.id}
                              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedBranches.includes(branch.id)}
                                onChange={() => handleBranchToggle(branch.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                {branch.name}
                              </span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
              disabled={loading}
            >
              {loading
                ? "Processing..."
                : isEditMode
                ? "Update"
                : `Add ${roleLabel}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddUserModal;
